import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './Button';
import { Input } from './Input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './Dialog';
import { useApi } from '../hooks/useApi';
import { ApiKey } from '../types/api';
import { Key, PencilSimple } from 'phosphor-react';
import { DeleteButton } from './DeleteButton';
import { Loader } from './Loader';

const getKeyFriendlyName = (keyName: string): string => {
  const names: Record<string, string> = {
    'kameone': 'Kameone - Credenciales',
    'kameone_usuario': 'Kameone - Usuario',
    'kameone_base_url': 'Kameone - Base URL',
    'kameone_bodega': 'Kameone - Bodega',
    'kameone_unidad_negocio': 'Kameone - Unidad de negocio',
    'kapso': 'Kapso - API Key',
    'whatsapp_phone_number': 'Kapso - Phone Number ID',
    'openai': 'OpenAI',
    'resend_api_key': 'Resend - API',
    'resend_inbox_email': 'Resend - Email de bandeja de entrada',
  };
  return names[keyName] || keyName;
};

const getValueLabel = (keyName: string): string => {
  switch (keyName) {
    case 'kameone': return 'Client ID:Client Secret';
    case 'kameone_usuario': return 'Email de Usuario';
    case 'kameone_base_url': return 'URL Base del API';
    case 'kameone_bodega': return 'Nombre de la Bodega';
    case 'kameone_unidad_negocio': return 'Nombre de la Unidad de Negocio';
    case 'resend_api_key': return 'API Key';
    case 'resend_inbox_email': return 'Email de bandeja de entrada';
    case 'whatsapp_phone_number': return 'Phone Number ID';
    default: return 'Valor';
  }
};

const getValuePlaceholder = (keyName: string): string => {
  switch (keyName) {
    case 'kameone': return 'client_id:client_secret';
    case 'kameone_usuario': return 'usuario@ejemplo.com';
    case 'kameone_base_url': return 'https://api.kameone.cl';
    case 'kameone_bodega': return 'ej: Central';
    case 'kameone_unidad_negocio': return 'ej: Administración';
    case 'resend_api_key': return 're_xxxxxxxxxxxxxxxxxxxx';
    case 'resend_inbox_email': return 'pedidos@tudominio.com';
    case 'whatsapp_phone_number': return 'ej: 123456789012345';
    default: return 'Ingresa el valor';
  }
};

const getValueHint = (keyName: string): string | null => {
  switch (keyName) {
    case 'kameone': return 'Formato: client_id:client_secret (separados por dos puntos)';
    case 'kameone_usuario': return 'Email del usuario para campo Usuario en Kameone';
    case 'kameone_base_url': return 'URL base del API de Kameone (ej: https://api.kameone.cl)';
    case 'kameone_bodega': return 'Nombre exacto de la bodega configurada en Kameone';
    case 'kameone_unidad_negocio': return 'Nombre exacto de la unidad de negocio configurada en Kameone';
    case 'resend_api_key': return 'API Key de Resend para recepción de emails (comienza con re_)';
    case 'resend_inbox_email': return 'Email configurado en Resend para recibir órdenes';
    case 'whatsapp_phone_number': return 'Phone Number ID de Kapso/Meta para enviar mensajes WhatsApp';
    default: return null;
  }
};

export const ApiKeysSection: React.FC<{ onSaved?: () => void }> = ({ onSaved }) => {
  const api = useApi();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [editMode, setEditMode] = useState<boolean>(false);
  const [keyName, setKeyName] = useState<string>('kameone');
  const [keyValue, setKeyValue] = useState<string>('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const loadApiKeys = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const fetchedKeys = await api.getApiKeys();
      setKeys(fetchedKeys);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar las credenciales');
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadApiKeys();
  }, [loadApiKeys]);

  const handleOpenDialog = () => {
    setEditMode(false);
    setKeyName('kameone');
    setKeyValue('');
    setValidationError(null);
    setIsDialogOpen(true);
  };

  const handleOpenEditDialog = async (keyNameToEdit: string) => {
    try {
      setIsLoadingEdit(true);
      setEditMode(true);
      setKeyName(keyNameToEdit);
      setValidationError(null);
      const keyData = await api.getApiKeyForEdit(keyNameToEdit);
      setKeyValue(keyData.value);
      setIsDialogOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar la credencial para editar');
    } finally {
      setIsLoadingEdit(false);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditMode(false);
    setKeyValue('');
    setValidationError(null);
  };

  const validateKey = (name: string, value: string): boolean => {
    switch (name) {
      case 'kameone': {
        const parts = value.split(':');
        if (parts.length !== 2) {
          setValidationError('Formato inválido. Debe ser: client_id:client_secret');
          return false;
        }
        if (!parts[0].trim() || !parts[1].trim()) {
          setValidationError('El client_id y client_secret no pueden estar vacíos');
          return false;
        }
        break;
      }
      case 'kameone_usuario': {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          setValidationError('Formato de email inválido');
          return false;
        }
        break;
      }
      case 'kameone_base_url': {
        try {
          new URL(value);
        } catch {
          setValidationError('URL inválida. Debe incluir protocolo (https://)');
          return false;
        }
        break;
      }
    }
    setValidationError(null);
    return true;
  };

  const handleSave = async () => {
    if (!keyValue.trim()) {
      setValidationError('El valor no puede estar vacío');
      return;
    }
    if (!validateKey(keyName, keyValue)) return;

    try {
      setIsSaving(true);
      setError(null);
      await api.setApiKey(keyName, keyValue);

      const action = editMode ? 'actualizada' : 'guardada';
      setSuccessMessage(`Credencial '${getKeyFriendlyName(keyName)}' ${action} exitosamente`);
      setTimeout(() => setSuccessMessage(null), 3000);
      onSaved?.();

      handleCloseDialog();
      await loadApiKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar la credencial');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (keyNameToDelete: string) => {
    try {
      setError(null);
      await api.deleteApiKey(keyNameToDelete);
      setSuccessMessage(`Credencial '${getKeyFriendlyName(keyNameToDelete)}' eliminada exitosamente`);
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadApiKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar la credencial');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader />
      </div>
    );
  }

  const hint = getValueHint(keyName);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-sans font-semibold text-xl text-foreground">
            Credenciales
          </h2>
          <p className="font-sans text-sm text-text-subtle mt-1">
            Gestiona las credenciales para integraciones externas
          </p>
        </div>
        <Button variant="primary" onClick={handleOpenDialog} className="gap-2">
          <Key size={16} weight="bold" />
          Agregar credencial
        </Button>
      </div>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md">
          <p className="font-sans text-sm">{successMessage}</p>
        </div>
      )}

      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-md">
          <p className="font-sans text-sm">{error}</p>
        </div>
      )}

      {keys.length === 0 ? (
        <div className="border border-border rounded-lg p-8 text-center">
          <Key size={48} className="mx-auto text-text-subtle mb-4" />
          <p className="font-sans text-base text-text-subtle">
            No hay credenciales configuradas
          </p>
          <p className="font-sans text-sm text-text-subtle mt-2">
            Agrega una credencial para comenzar a usar las integraciones
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {keys.map((key) => (
            <div
              key={key.keyName}
              className="border border-border rounded-lg p-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                  <Key size={20} weight="bold" className="text-primary" />
                </div>
                <div>
                  <p className="font-sans font-semibold text-base text-foreground">
                    {getKeyFriendlyName(key.keyName)}
                  </p>
                  <p className="font-sans text-sm text-text-subtle">
                    Última actualización: {new Date(key.updatedAt).toLocaleDateString('es-CL')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleOpenEditDialog(key.keyName)}
                  className="text-primary hover:bg-primary/10"
                  disabled={isLoadingEdit}
                >
                  <PencilSimple size={18} weight="bold" />
                </Button>
                <DeleteButton
                  onDelete={() => handleDelete(key.keyName)}
                  size="md"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="p-6">
          <DialogHeader className="mb-4">
            <DialogTitle>{editMode ? 'Editar' : 'Agregar'} credencial</DialogTitle>
            <DialogDescription>
              {editMode
                ? 'Modifica la credencial existente'
                : 'Ingresa las credenciales para integraciones externas'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="font-sans font-medium text-sm text-foreground block mb-2">
                Tipo de credencial
              </label>
              <select
                value={keyName}
                onChange={(e) => { setKeyName(e.target.value); setValidationError(null); }}
                className="w-full rounded-md border-2 border-border bg-background px-4 py-3 font-sans font-medium text-base outline-none focus:border-primary"
                disabled={isSaving || editMode}
              >
                <option value="kameone">Kameone - Credenciales</option>
                <option value="kameone_usuario">Kameone - Usuario (Email)</option>
                <option value="kameone_base_url">Kameone - Base URL</option>
                <option value="kameone_bodega">Kameone - Bodega</option>
                <option value="kameone_unidad_negocio">Kameone - Unidad de negocio</option>
                <option value="kapso">Kapso - API Key</option>
                <option value="whatsapp_phone_number">Kapso - Phone Number ID</option>
                <option value="openai">OpenAI</option>
                <option value="resend_api_key">Resend - API</option>
                <option value="resend_inbox_email">Resend - Email de bandeja de entrada</option>
              </select>
              {editMode && (
                <p className="font-sans text-xs text-text-subtle mt-1">
                  El tipo de credencial no se puede cambiar al editar
                </p>
              )}
            </div>

            <div>
              <label className="font-sans font-medium text-sm text-foreground block mb-2">
                {getValueLabel(keyName)}
              </label>
              <Input
                type="text"
                value={keyValue}
                onChange={(e) => { setKeyValue(e.target.value); setValidationError(null); }}
                placeholder={getValuePlaceholder(keyName)}
                error={!!validationError}
                disabled={isSaving}
              />
              {validationError && (
                <p className="font-sans text-sm text-destructive mt-1">{validationError}</p>
              )}
              {hint && (
                <p className="font-sans text-xs text-text-subtle mt-1">{hint}</p>
              )}
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="ghost" onClick={handleCloseDialog} disabled={isSaving}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaving || !keyValue.trim()}
              className="gap-2"
            >
              {isSaving ? <><Loader />Guardando...</> : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
